import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Advent of Code Day 13, Packet Scanners",
  "description": "",
  "published": "2017-12-13T00:00:00.000Z",
  "redirect_from": ["/blog/advent-code-day-13-packet-scanners/swizec/7961"],
  "categories": "Haskell, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "3abrq1N5ITU",
      "videostartat": "0"
    }}></lite-youtube>
    <p><a parentName="p" {...{
        "href": "https://adventofcode.com/"
      }}>{`Advent of Code`}</a>{` is an advent calendar for coders. A daily puzzle that exercises that algorithmic part of coding that you don't usually get to use in day to day programming.`}</p>
    <p>{`Here's what mine looks like right now 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2017/12/Screen-Shot-2017-12-13-at-08.14.34-inline.png",
        "alt": null
      }}></img></p>
    <p>{`The game works like this: Every day at midnight, a new puzzle opens up. It follows a storyline of traveling through a computer. You start with some memory access, go through registers and CPUs, and on Day 13, we dealt with some firewalls.`}</p>
    <p><a parentName="p" {...{
        "href": "https://www.youtube.com/playlist?list=PLF8WgaD4xmjWaUkBRoQiYGCV3WSKRH0pU"
      }}>{`I've been playing with it`}</a>{` in Haskell and Python. Python because it's perfect for algorithmic puzzles like this, Haskell because I wanted to brush up. Been years since I did Haskell. I miss Haskell.`}</p>
    <p>{`JavaScript is clunky for this sort of thing. 🤷‍♀️`}</p>
    <p>{`You can `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/advent-of-code-2017"
      }}>{`see my code on Github`}</a>{`. You should join my leaderboard, too. Some friendly competition that I am currently losing. 👉 `}<inlineCode parentName="p">{`193241-e9880d10`}</inlineCode></p>
    <h2 {...{
      "id": "packet-scanners-in-haskell"
    }}>{`Packet Scanners in Haskell`}</h2>
    <p>{`On Day 13, we had to fly through a firewall without getting found by the scanners.`}</p>
    <blockquote>
      <p parentName="blockquote">{`You need to cross a vast firewall. The firewall consists of several layers, each with a security scanner that moves back and forth across the layer. To succeed, you must not be detected by a scanner.`}</p>
      <p parentName="blockquote">{`By studying the firewall briefly, you are able to record (in your puzzle input) the depth of each layer and the range of the scanning area for the scanner within it, written as depth: range. Each layer has a thickness of exactly 1. A layer at depth 0 begins immediately inside the firewall; a layer at depth 1 would start immediately after that.`}</p>
    </blockquote>
    <p>{`Visually that looks like this:`}</p>
    <pre><code parentName="pre" {...{}}>{`Picosecond 0:
 0   1   2   3   4   5   6
[S] [S] ... ... [S] ... [S]
[ ] [ ]         [ ]     [ ]
[ ]             [ ]     [ ]
                [ ]     [ ]
`}</code></pre>
    <p>{`Vertical columns are the firewall layers, `}<inlineCode parentName="p">{`S`}</inlineCode>{` symbols are the packet scanners. Each picosecond they move down by `}<inlineCode parentName="p">{`1`}</inlineCode>{`. At the bottom, they bounce back.`}</p>
    <p>{`We move one layer to the right on each picosecond. Always on the top row. Each time we are seen by a scanner (same position at same time), it adds `}<inlineCode parentName="p">{`depth*range`}</inlineCode>{` to our travel cost.`}</p>
    <h3 {...{
      "id": "star-1"
    }}>{`Star 1`}</h3>
    <p>{`For the first star, we have to calculate how much it costs to get through the scanner.`}</p>
    <p>{`Rather than simulate the firewall and our travel through it, we can rely on two key insights:`}</p>
    <ol>
      <li parentName="ol">{`The depth of a layer is the same as the number of picoseconds it takes to get to it. Layer 0 is at 0 picoseconds, layer 10 at 10, 90 at 90, and so on.`}</li>
      <li parentName="ol">{`Scanners return to their 0th position periodically. We can calculate if a scanner is at 0 for any particular picosecond.`}</li>
    </ol>
    <p>{`Finding the series formula for #2 was the tricky part because I suck at finding series formulas by inference. This is the pattern 👇`}</p>
    <pre><code parentName="pre" {...{}}>{`A scanner with range 1, is at 0 every 1 picosecond.

With range 2, every 2 picoseconds
3, every 4 picoseconds
4, every 6 picoseconds
5, every 8
6, every 10
...
`}</code></pre>
    <p>{`You might find it obvious, but it wrinkled my brain six ways to Sunday. A scanner returns to 0 every `}<inlineCode parentName="p">{`range*2-2`}</inlineCode>{` picoseconds.`}</p>
    <p>{`That means we can see if a scanner is at zero at any particular time by checking the modulus with time 👉 `}<inlineCode parentName="p">{`time % (range*2-2) == 0`}</inlineCode></p>
    <p>{`Since we know we reach each layer at the depth of that layer, we can substitute `}<inlineCode parentName="p">{`time`}</inlineCode>{` for `}<inlineCode parentName="p">{`depth`}</inlineCode>{` and calculate the cost, or severity, at each layer with this function 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`severity::(Int, Int) -> Int
severity (depth, range)
    | mod depth (range*2-2) == 0 = depth * range
    | otherwise = 0
`}</code></pre>
    <p><inlineCode parentName="p">{`severity`}</inlineCode>{` takes a tuple of two integers, a depth and a range, and checks if the scanner on that layer is at position `}<inlineCode parentName="p">{`0`}</inlineCode>{` when we reach it. If it is, it returns `}<inlineCode parentName="p">{`depth*range`}</inlineCode>{` the cost of getting caught; otherwise, it returns `}<inlineCode parentName="p">{`0`}</inlineCode>{`. No cost.`}</p>
    <p>{`We can then use this function to `}<inlineCode parentName="p">{`sum`}</inlineCode>{` the cost of our entire trip.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`star1::[String] -> Int
star1 input = sum $ map severity $ prepInput input
`}</code></pre>
    <p><inlineCode parentName="p">{`star`}</inlineCode>{` takes a list of strings and returns an integer. First, it parses the list of strings into a list of `}<inlineCode parentName="p">{`(depth, range)`}</inlineCode>{` tuples. That's `}<inlineCode parentName="p">{`prepInput`}</inlineCode>{`, and it's `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/advent-of-code-2017/blob/master/13.hs#L50"
      }}>{`not very interesting`}</a>{`.`}</p>
    <p>{`After the input is parsed, we `}<inlineCode parentName="p">{`map`}</inlineCode>{` through with the `}<inlineCode parentName="p">{`severity`}</inlineCode>{` function to get a list of costs for every layer, and `}<inlineCode parentName="p">{`sum`}</inlineCode>{` them up. That's our result.`}</p>
    <h3 {...{
      "id": "star-2"
    }}>{`Star 2`}</h3>
    <p>{`Star 2 adds an interesting twist to the story. We have to avoid getting caught.`}</p>
    <blockquote>
      <p parentName="blockquote">{`Now, you need to pass through the firewall without being caught - easier said than done.`}</p>
      <p parentName="blockquote">{`You can't control the speed of the packet, but you can delay it any number of picoseconds. For each picosecond you delay the packet before beginning your trip, all security scanners move one step. You're not in the firewall during this time; you don't enter layer 0 until you stop delaying the packet.`}</p>
    </blockquote>
    <p>{`Our goal is to find the smallest delay that gets us through the firewall uncaught.`}</p>
    <p>{`Uncaught is the same as cost of the trip being 0. That means we can use the `}<inlineCode parentName="p">{`severity`}</inlineCode>{` function from before.`}</p>
    <p>{`Since we think of each layer's depth as the picosecond when we reach that layer, we can fake the delay by making the entire firewall deeper. That means adding `}<inlineCode parentName="p">{`+1`}</inlineCode>{` to every layer until the cost of our trip becomes zero.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`smallestDelay::[(Int, Int)] -> Int
smallestDelay input
    | sum severities == 0 = delay
    | otherwise = smallestDelay $ map (\\(a,b) -> (a+1, b)) input
    where severities = map severity input
          (delay, _) = head input
`}</code></pre>
    <p><inlineCode parentName="p">{`smallestDelay`}</inlineCode>{` takes a list of `}<inlineCode parentName="p">{`(depth, range)`}</inlineCode>{` tuples and returns an integer. If the `}<inlineCode parentName="p">{`sum severities`}</inlineCode>{` (cost of our trip) is `}<inlineCode parentName="p">{`0`}</inlineCode>{`, we return the current delay; otherwise, we construct a new faux firewall whose layers are shifted by `}<inlineCode parentName="p">{`+1`}</inlineCode>{`.`}</p>
    <p>{`Each time we do this, we use `}<inlineCode parentName="p">{`map severity input`}</inlineCode>{` to get a list of costs and take the `}<inlineCode parentName="p">{`head`}</inlineCode>{` of our input firewall to see the delay. Since we're shifting the whole firewall, the "depth" of the first layer represents our delay.`}</p>
    <p>{`And that's the solution in Haskell 🤓`}</p>
    <p>{`Happy hacking.`}</p>
    <p>{`PS: in `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=3abrq1N5ITU"
      }}>{`the video`}</a>{`, you can see me trying to solve Day 12 as well. That's because I'm behind and playing catchup.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      